// iPOINT API service
// Connects the React frontend to the deployed Node.js backend.

import { getToken as getUserToken } from './userAuth'

export const USING_MOCK = false

const API = (
  process.env.REACT_APP_API_URL ||
  'https://ipointshop-com-127859.hostingersite.com'
).replace(/\/$/, '')

const TOKEN_KEY = 'ipoint-admin-token'

// Convert relative image/video paths to public backend URLs.
export const imageSrc = (value) => {
  if (!value) return ''

  const url = String(value).trim()

  if (!url) return ''

  // Handle absolute URLs, including old localhost upload URLs.
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url)

      const isLocalhost =
        parsed.hostname === 'localhost' ||
        parsed.hostname === '127.0.0.1'

      if (
        isLocalhost &&
        parsed.pathname.startsWith('/uploads/')
      ) {
        return `${API}${parsed.pathname}${parsed.search}${parsed.hash}`
      }

      return url
    } catch {
      return url
    }
  }

  // Handle protocol-relative URLs, e.g. //example.com/image.jpg
  if (url.startsWith('//')) {
    return `https:${url}`
  }

  // Handle relative paths, e.g. /uploads/example.jpg
  if (url.startsWith('/')) {
    return `${API}${url}`
  }

  // Handle paths without a leading slash.
  return `${API}/${url}`
}

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export const setToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token)
    } else {
      localStorage.removeItem(TOKEN_KEY)
    }
  } catch {
    // Storage may be unavailable.
  }
}

export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    // Storage may be unavailable.
  }
}

// Common API request handler.
// customer: true sends the signed-in customer's token instead of the admin token.
async function request(
  path,
  { method = 'GET', body, json = false, customer = false } = {}
) {
  const headers = {}
  const token = customer ? getUserToken() : getToken()

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  if (json) {
    headers['Content-Type'] = 'application/json'
  }

  let response

  try {
    response = await fetch(`${API}${path}`, {
      method,
      headers,
      body: json ? JSON.stringify(body) : body,
    })
  } catch (error) {
    console.error(
      `[iPOINT] Network request failed: ${method} ${path}`,
      error
    )

    throw new Error(
      'Cannot reach the server. Check your internet connection and try again.'
    )
  }

  let data = null

  try {
    data = await response.json()
  } catch {
    // The response may have no JSON body.
  }

  if (!response.ok) {
    if (
      response.status === 401 &&
      token &&
      !customer &&
      path !== '/auth/login'
    ) {
      clearToken()

      if (typeof window !== 'undefined') {
        window.location.assign('/admin/login')
      }
    }

    const error = new Error(
      (data && data.message) ||
        `Request failed with status ${response.status}.`
    )

    error.status = response.status

    throw error
  }

  return data
}

// Admin login.
export const api = {
  login: (email, password) =>
    request('/auth/login', {
      method: 'POST',
      json: true,
      body: { email, password },
    }),

  // Customer places an order (public; called after a successful payment).
  createOrder: (order) =>
    request('/orders', { method: 'POST', json: true, body: order }),

  // Asks our server to create the Razorpay order (amount is calculated server-side).
  createPaymentOrder: (payload) =>
    request('/payments/create-order', { method: 'POST', json: true, body: payload, customer: true }),

  // Confirms the payment with the server (checks Razorpay's signature, marks the order paid).
  verifyPayment: (payload) =>
    request('/payments/verify', { method: 'POST', json: true, body: payload, customer: true }),

  // The signed-in customer's own orders (paid ones, newest first).
  myOrders: () => request('/orders/mine', { customer: true }),

  // List resources; pass true to request all records.
  list: (name, all = false) =>
    request(`/${name}${all ? '?all=1' : ''}`),

  // Create a resource using FormData.
  create: (name, formData) =>
    request(`/${name}`, {
      method: 'POST',
      body: formData,
    }),

  // Update a resource using FormData.
  update: (name, id, formData) =>
    request(`/${name}/${id}`, {
      method: 'PUT',
      body: formData,
    }),

  // Delete a resource.
  remove: (name, id) =>
    request(`/${name}/${id}`, {
      method: 'DELETE',
    }),
}